// Omnix/client/ui/EmailWizard.js
import { ComponentBase } from "../core/ComponentBase.js";

export class EmailWizard extends ComponentBase {
    static styles = `
        .email-wizard-modal .modal-dialog { max-width: 600px; }
        .wizard-step-content { display: none; }
        .wizard-step-content.active { display: block; }
        
        /* Styles for the provider selection step */
        .provider-selection-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
            margin-top: 1.5rem;
        }
        .provider-card {
            padding: 1.5rem 1rem;
            border: 1px solid var(--bs-border-color);
            border-radius: var(--bs-border-radius-lg);
            text-align: center;
            cursor: pointer;
            transition: all 0.2s ease-in-out;
        }
        .provider-card:hover {
            transform: translateY(-3px);
            box-shadow: var(--bs-box-shadow);
            border-color: var(--bs-primary);
        }
        .provider-card i {
            font-size: 2.5rem;
            margin-bottom: 0.75rem;
        }
        .provider-card-title {
            font-weight: 500;
        }
        .manual-setup-link {
            display: block;
            text-align: center;
            margin-top: 2rem;
        }
    `;

    constructor(config) {
        super(config);
        this.modalInstance = null;
        this.currentStep = 'selection'; // 'selection', 'smtp', 'oauth_pending'
        this.applyStyles();
    }

    async initialize() {
        this.container = document.createElement('div');
        this.container.className = 'modal fade email-wizard-modal';
        this.container.id = `${this.config.instanceId}-modal`;
        this.container.setAttribute('tabindex', '-1');

        // The modal structure is now a shell, content is rendered dynamically
        this.container.innerHTML = `
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="email-wizard-title"></h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <form onsubmit="return false;" autocomplete="off" class="modal-body" id="email-wizard-body"></form>
                    <div class="modal-footer" id="email-wizard-footer"></div>
                </div>
            </div>
        `;
        this.modalInstance = new bootstrap.Modal(this.container);

        this.titleEl = this.container.querySelector('#email-wizard-title');
        this.bodyEl = this.container.querySelector('#email-wizard-body');
        this.footerEl = this.container.querySelector('#email-wizard-footer');

        await super.initialize();
    }

    show() {
        this.currentStep = 'selection';
        this._renderCurrentStep();
        this.modalInstance.show();
    }

    _renderCurrentStep() {
        switch (this.currentStep) {
            case 'selection':
                this._renderSelectionStep();
                break;
            case 'smtp':
                this._renderSmtpStep();
                break;
            case 'oauth_pending':
                this._renderOAuthPendingStep();
                break;
        }
    }

    _renderSelectionStep() {
        this.titleEl.textContent = this.t('Connect an Email Account');
        this.bodyEl.innerHTML = `
            <p class="text-muted text-center">${this.t('Choose your email provider for a fast and secure connection, or set up manually using IMAP/SMTP.')}</p>
            <div class="provider-selection-grid">
                <div class="provider-card" data-oauth-provider="google-channel">
                    <i class="bi bi-google text-danger"></i>
                    <div class="provider-card-title">${this.t('Google / Gmail')}</div>
                </div>
                <div class="provider-card" data-oauth-provider="microsoft-channel">
                    <i class="bi bi-microsoft text-primary"></i>
                    <div class="provider-card-title">${this.t('Microsoft 365 / Outlook')}</div>
                </div>
            </div>
            <a href="#" class="manual-setup-link" data-step="smtp">${this.t('Set up manually (IMAP/SMTP)')}</a>
        `;
        this.footerEl.innerHTML = `<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">${this.t('Cancel')}</button>`;

        this.bodyEl.querySelectorAll('[data-oauth-provider]').forEach(card => {
            card.addEventListener('click', () => this.initiateOAuthPopup(card.dataset.oauthProvider));
        });
        this.bodyEl.querySelector('[data-step="smtp"]').addEventListener('click', (e) => {
            e.preventDefault();
            this.currentStep = 'smtp';
            this._renderCurrentStep();
        });
    }

    async _renderSmtpStep() {
        this.titleEl.textContent = this.t('Manual Email Setup (IMAP/SMTP)');
        this.bodyEl.innerHTML = `
            <p class="text-muted">${this.t('Enter the connection details for your IMAP/SMTP email account.')}</p>
            <div class="mb-3"><label class="form-label">${this.t('Account Name')}</label><input type="text" id="email-name" class="form-control" placeholder="e.g., Main Support Email"></div>
            <h6 class="mt-4">${this.t('Routing')}</h6>
            <div class="mb-3">
                <label for="email-workflow-select" class="form-label">${this.t('Inbound Workflow')}</label>
                <select id="email-workflow-select" class="form-select"><option value="">${this.t('Default (Direct to Inbox)')}</option></select>
                <div class="form-text">${this.t("Select a workflow to handle incoming emails. Workflows must be tagged with 'messaging'.")}</div>
            </div>
            <h6 class="mt-4">${this.t('Outgoing Mail (SMTP)')}</h6>
            <div class="row">
                <div class="col-md-8"><label class="form-label">SMTP Host</label><input type="text" id="email-smtp-host" class="form-control"></div>
                <div class="col-md-4"><label class="form-label">SMTP Port</label><input type="number" id="email-smtp-port" class="form-control"></div>
            </div>
            <div class="row mt-2">
                <div class="col-md-6"><label class="form-label">SMTP Username</label><input type="text" id="email-smtp-user" class="form-control"></div>
                <div class="col-md-6"><label class="form-label">SMTP Password</label><input type="password" id="email-smtp-pass" class="form-control"></div>
            </div>
            <div class="form-check mt-2"><input class="form-check-input" type="checkbox" id="email-smtp-secure"><label class="form-check-label" for="email-smtp-secure">${this.t('Use SSL/TLS for SMTP')}</label></div>
            <h6 class="mt-4">${this.t('Incoming Mail (IMAP)')}</h6>
            <div class="row">
                <div class="col-md-8"><label class="form-label">IMAP Host</label><input type="text" id="email-imap-host" class="form-control"></div>
                <div class="col-md-4"><label class="form-label">IMAP Port</label><input type="number" id="email-imap-port" class="form-control"></div>
            </div>
            <div class="row mt-2">
                <div class="col-md-6"><label class="form-label">IMAP Username</label><input type="text" id="email-imap-user" class="form-control"></div>
                <div class="col-md-6"><label class="form-label">IMAP Password</label><input type="password" id="email-imap-pass" class="form-control"></div>
            </div>
            <div class="form-check mt-2"><input class="form-check-input" type="checkbox" id="email-imap-tls"><label class="form-check-label" for="email-imap-tls">${this.t('Use TLS for IMAP')}</label></div>
        `;
        this.footerEl.innerHTML = `
            <button type="button" class="btn btn-secondary" id="wizard-back-btn">${this.t('Back')}</button>
            <button type="button" class="btn btn-primary" id="wizard-finish-btn">${this.t('Test & Save')}</button>
        `;
        this.footerEl.querySelector('#wizard-back-btn').addEventListener('click', () => {
            this.currentStep = 'selection';
            this._renderCurrentStep();
        });
        this.footerEl.querySelector('#wizard-finish-btn').addEventListener('click', this.handleSmtpFinish.bind(this));
        await this._populateWorkflows();
    }

    _renderOAuthPendingStep() {
        this.titleEl.textContent = this.t('Connecting...');
        this.bodyEl.innerHTML = `
            <div class="text-center p-4">
                <div class="spinner-border text-primary" role="status" style="width: 3rem; height: 3rem;"></div>
                <p class="mt-3 text-muted">${this.t('Waiting for authentication in the popup window...')}</p>
            </div>
        `;
        this.footerEl.innerHTML = `<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">${this.t('Cancel')}</button>`;
    }

    async initiateOAuthPopup(provider) {
        this.currentStep = 'oauth_pending';
        this._renderCurrentStep();

        try {
            const result = await this.appClient.rpcClient.call('AuthRpc.getScopedOAuthLoginUrl', {
                provider: provider,
                purpose: 'connect_email_channel',
                successRedirect: new URL('/admin/channels', window.location.origin).href,
                failureRedirect: new URL('/admin/channels', window.location.origin).href
            });

            if (!result.authUrl) throw new Error("Server did not return an authentication URL.");

            const popup = window.open(result.authUrl, 'oauthPopup', 'width=600,height=700,scrollbars=yes');

            const messageListener = (event) => {
                if (event.origin !== window.location.origin) return;
                const { success, provider: returnedProvider } = event.data;
                if (success) {
                    this.appClient.notifier.success(`Successfully connected with ${returnedProvider}!`);
                    this.emit('channelSaved');
                    this.modalInstance.hide();
                } else {
                    this.appClient.notifier.error(`Failed to connect with ${returnedProvider}. Please try again.`);
                    this.currentStep = 'selection'; // Go back to selection on failure
                    this._renderCurrentStep();
                }
                cleanup();
            };

            const cleanup = () => {
                window.removeEventListener('message', messageListener);
                if (popupTimer) clearInterval(popupTimer);
                if (popup) popup.close();
            };

            window.addEventListener('message', messageListener);

            const popupTimer = setInterval(() => {
                if (popup && popup.closed) {
                    cleanup();
                    this.log.info("OAuth popup was closed by the user.");
                    if (this.currentStep === 'oauth_pending') { // Only go back if we're still on the pending screen
                        this.currentStep = 'selection';
                        this._renderCurrentStep();
                    }
                }
            }, 500);

        } catch (error) {
            this.appClient.notifier.error(`Could not initiate connection: ${error.message}`);
            this.currentStep = 'selection'; // Revert to selection step on error
            this._renderCurrentStep();
        }
    }

    async _populateWorkflows() {
        const select = this.container.querySelector('#email-workflow-select');
        if (!select) return;
        try {
            const workflows = await this.appClient.rpcClient.call('ChannelManagerRpc.getFieldValues', {
                fieldName: 'inboundWorkflowId',
                reference: {
                    collection: 'workflows', valueField: '_id', displayField: 'name',
                    filter: { tags: 'messaging' }
                }
            });
            select.innerHTML = '<option value="">Default (Direct to Inbox)</option>';
            workflows.forEach(wf => {
                const option = document.createElement('option');
                option.value = wf.value;
                option.textContent = wf.label;
                select.appendChild(option);
            });
        } catch (error) {
            this.log.error("Failed to populate workflows in EmailWizard:", error);
        }
    }


    async handleSmtpFinish() {
        const finishBtn = this.container.querySelector('#wizard-finish-btn');
        finishBtn.disabled = true;
        finishBtn.innerHTML = `<span class="spinner-border spinner-border-sm"></span> Testing...`;

        const workflowId = this.container.querySelector('#email-workflow-select').value;

        const config = {
            name: this.container.querySelector('#email-name').value.trim(),
            channelType: 'email',
            provider: 'smtp',
            isEnabled: true,
            inboundWorkflowId: workflowId || null,
            settings: {
                smtpHost: this.container.querySelector('#email-smtp-host').value.trim(),
                smtpPort: parseInt(this.container.querySelector('#email-smtp-port').value, 10),
                smtpUser: this.container.querySelector('#email-smtp-user').value.trim(),
                smtpPassword: this.container.querySelector('#email-smtp-pass').value,
                smtpSecure: this.container.querySelector('#email-smtp-secure').checked,
                imapHost: this.container.querySelector('#email-imap-host').value.trim(),
                imapPort: parseInt(this.container.querySelector('#email-imap-port').value, 10),
                imapUser: this.container.querySelector('#email-imap-user').value.trim(),
                imapPassword: this.container.querySelector('#email-imap-pass').value,
                imapTls: this.container.querySelector('#email-imap-tls').checked,
            }
        };

        try {
            // 1. Validate credentials with the server before creating
            await this.appClient.rpcClient.call('ChannelManagerRpc.setupProvider', {
                providerName: 'smtp',
                command: 'wizard_validate_credentials',
                args: { settings: config.settings }
            });

            // 2. If validation passes, create the channel
            await this.appClient.rpcClient.call('ChannelManagerRpc.create', config);

            this.appClient.notifier.success(`Email channel '${config.name}' configured successfully!`);
            this.emit('channelSaved');
            this.modalInstance.hide();
        } catch (error) {
            this.log.error("Failed to configure Email channel:", error);
            this.appClient.notifier.error(`Setup failed: ${error.message}`);
        } finally {
            finishBtn.disabled = false;
            finishBtn.textContent = 'Test & Save';
        }
    }


    render() { return this.container; }
    destroy() { this.modalInstance?.dispose(); this.container?.remove(); super.destroy(); }
}
